import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Reveal from "./Reveal";
import { personal } from "../data/portfolio";

gsap.registerPlugin(ScrollTrigger);

const contactLinks = [
  { label: "GitHub", href: personal.github },
  { label: "LinkedIn", href: personal.linkedin },
  { label: "Email", href: `mailto:${personal.email}` },
];

export default function Contact() {
  const headingRef = useRef(null);

  useEffect(() => {
    if (!headingRef.current) return;
    gsap.fromTo(
      headingRef.current,
      { backgroundSize: "0% 3px" },
      {
        backgroundSize: "100% 3px",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 80%",
        },
        duration: 1,
        ease: "power3.out",
        delay: 0.5,
      }
    );
  }, []);

  return (
    <section
      id="contact"
      style={{
        padding: "8rem 3rem",
        maxWidth: 1200,
        margin: "0 auto",
        textAlign: "center",
        position: "relative",
      }}
    >
      {/* Background glow */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(57,255,20,0.04) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Reveal>
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.72rem",
            letterSpacing: "0.22em",
            color: "var(--accent)",
            textTransform: "uppercase",
            marginBottom: "1.5rem",
          }}
        >
            {/* 03. Contact */}
        </p>
      </Reveal>

      <Reveal delay={0.05}>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(4.5rem, 12vw, 11rem)",
            lineHeight: 0.88,
            marginBottom: "1.5rem",
          }}
        >
          LET'S
          <br />
          WORK
          <span style={{ color: "var(--accent)" }}>.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <p
          style={{
            fontSize: "1rem",
            color: "var(--muted2)",
            maxWidth: 480,
            margin: "0 auto 3rem",
            lineHeight: 1.75,
          }}
        >
          Open to full-stack roles, internships, and exciting collaborations.
          Let's build something great together.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          {contactLinks.map((link) => (
            <a key={link.label} href={link.href} target="_blank" rel="noreferrer">
              <motion.button
                whileHover={{
                  borderColor: "var(--accent)",
                  color: "var(--accent)",
                  y: -3,
                }}
                whileTap={{ scale: 0.97 }}
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "0.72rem",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "0.9rem 2.2rem",
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "none",
                  transition: "all 0.2s",
                }}
              >
                {link.label} ↗
              </motion.button>
            </a>
          ))}
        </div>
      </Reveal>

      {/* Divider */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
        style={{
          height: 1,
          background: "linear-gradient(90deg, transparent, var(--border), transparent)",
          marginTop: "6rem",
          transformOrigin: "center",
        }}
      />
    </section>
  );
}
